/**
 * BFCM event timeline — date progress + MO/TA tap-to-switch phase caption (Figma 1037:4319).
 */
class BfcmEventTimeline extends HTMLElement {
  /** @type {(() => void) | null} */
  #compactMqListener = null;

  /** @type {MediaQueryList | null} */
  #compactMq = null;

  connectedCallback() {
    this.#initProgress();
    this.#bindCompactInteraction();
  }

  disconnectedCallback() {
    if (this.#compactMq && this.#compactMqListener) {
      this.#compactMq.removeEventListener('change', this.#compactMqListener);
    }
  }

  #initProgress() {
    if (this.dataset.progressInit === 'true') return;

    const board = this.querySelector('[data-bfcm-timeline-board]');
    if (!board) return;

    const phaseEls = [...board.querySelectorAll('[data-phase-start]')];
    if (phaseEls.length === 0) return;

    const phases = phaseEls.map((el) => ({
      el,
      start: parseDateBoundary(el.dataset.phaseStart, false),
      end: parseDateBoundary(el.dataset.phaseEnd, true),
    }));

    const now = Date.now();
    let activeIndex = -1;

    for (let i = 0; i < phases.length; i += 1) {
      const { start, end } = phases[i];
      if (Number.isNaN(start) || Number.isNaN(end)) continue;
      if (now >= start && now <= end) {
        activeIndex = i;
        break;
      }
      if (now > end) {
        activeIndex = i;
      }
    }

    if (now < phases[0].start) {
      activeIndex = -1;
    }

    const defaultSelected = activeIndex >= 0 ? activeIndex : 0;
    this.dataset.progressPhaseIndex = String(activeIndex);
    this.dataset.selectedPhaseIndex = String(defaultSelected);

    this.#applyProgressVisuals(board, activeIndex, now, phases);

    this.dataset.progressInit = 'true';
    this.#observeTrack(board);
  }

  /** @param {HTMLElement} board */
  #getProgressPhaseIndex(board) {
    const raw = Number(this.dataset.progressPhaseIndex);
    return Number.isNaN(raw) ? -1 : raw;
  }

  /** @param {HTMLElement} board */
  #getSelectedPhaseIndex(board) {
    const raw = Number(this.dataset.selectedPhaseIndex);
    return Number.isNaN(raw) ? 0 : raw;
  }

  /** @type {ResizeObserver | null} */
  #trackResizeObserver = null;

  /** @param {HTMLElement} board */
  #observeTrack(board) {
    const track = board.querySelector('.bfcm-event-timeline__track');
    if (!track || this.#trackResizeObserver) return;

    this.#trackResizeObserver = new ResizeObserver(() => {
      if (board.classList.contains('is-compact')) {
        this.#layoutTrackCompact(board, this.#getProgressPhaseIndex(board));
      }
    });
    this.#trackResizeObserver.observe(track);
  }

  #bindCompactInteraction() {
    this.#compactMq = window.matchMedia('(max-width: 1279px)');
    this.#compactMqListener = () => this.#syncCompactMode();
    this.#compactMq.addEventListener('change', this.#compactMqListener);
    this.#syncCompactMode();
  }

  #syncCompactMode() {
    const board = this.querySelector('[data-bfcm-timeline-board]');
    if (!board) return;

    const isCompact = this.#compactMq?.matches ?? false;
    board.classList.toggle('is-compact', isCompact);

    const phaseTops = [...board.querySelectorAll('.bfcm-event-timeline__phase-top')];

    phaseTops.forEach((el) => {
      if (isCompact) {
        el.setAttribute('role', 'button');
        el.setAttribute('tabindex', '0');
        if (!el.dataset.compactBound) {
          el.addEventListener('click', this.#onPhaseActivate);
          el.addEventListener('keydown', this.#onPhaseKeydown);
          el.dataset.compactBound = 'true';
        }
      } else {
        el.removeAttribute('role');
        el.removeAttribute('tabindex');
      }
    });

    if (isCompact) {
      const progressIndex = this.#getProgressPhaseIndex(board);
      this.#applyProgressVisuals(board, progressIndex, Date.now(), this.#getPhasesFromBoard(board));
      this.#applySelectedVisuals(board, this.#getSelectedPhaseIndex(board));
      this.#updateCaptionPanel(board, this.#getSelectedPhaseIndex(board));
    }
  }

  /** @param {HTMLElement} board */
  #getPhasesFromBoard(board) {
    return [...board.querySelectorAll('[data-phase-start]')].map((el) => ({
      start: parseDateBoundary(el.dataset.phaseStart, false),
      end: parseDateBoundary(el.dataset.phaseEnd, true),
    }));
  }

  /** @type {(event: Event) => void} */
  #onPhaseActivate = (event) => {
    const board = this.querySelector('[data-bfcm-timeline-board]');
    if (!board?.classList.contains('is-compact')) return;

    const target = /** @type {HTMLElement} */ (event.currentTarget);
    const index = Number(target.dataset.phaseIndex);
    if (Number.isNaN(index)) return;

    this.#selectCompactPhase(board, index);
  };

  /** @type {(event: KeyboardEvent) => void} */
  #onPhaseKeydown = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.#onPhaseActivate(event);
  };

  /**
   * MO/TA tap — caption + label colors only; progress stays date-driven.
   * @param {HTMLElement} board
   * @param {number} index
   */
  #selectCompactPhase(board, index) {
    this.dataset.selectedPhaseIndex = String(index);
    this.#applySelectedVisuals(board, index);
    this.#updateCaptionPanel(board, index);
  }

  /**
   * @param {HTMLElement} board
   * @param {number} selectedIndex
   */
  #applySelectedVisuals(board, selectedIndex) {
    if (!board.classList.contains('is-compact')) return;

    board.querySelectorAll('.bfcm-event-timeline__phase-top[data-phase-index]').forEach((el) => {
      const index = Number(/** @type {HTMLElement} */ (el).dataset.phaseIndex);
      /** @type {HTMLElement} */ (el).classList.toggle('is-selected', index === selectedIndex);
    });
  }

  /**
   * @param {HTMLElement} board
   * @param {number} index
   */
  #updateCaptionPanel(board, index) {
    const detail = board.querySelector('[data-bfcm-phase-detail]');
    if (!detail) return;

    detail.querySelectorAll('[data-phase-index]').forEach((panel) => {
      const panelIndex = Number(/** @type {HTMLElement} */ (panel).dataset.phaseIndex);
      /** @type {HTMLElement} */ (panel).hidden = panelIndex !== index;
    });
  }

  /**
   * Track + dots (+ desktop labels) from calendar progress only.
   * @param {HTMLElement} board
   * @param {number} progressIndex
   * @param {number} now
   * @param {{ start: number, end: number }[]} phases
   */
  #applyProgressVisuals(board, progressIndex, now, phases) {
    const phaseEls = [...board.querySelectorAll('.bfcm-event-timeline__phase-top[data-phase-index]')];
    const bottomPhases = [...board.querySelectorAll('.bfcm-event-timeline__phase-grid--bottom .bfcm-event-timeline__phase')];
    const dots = [...board.querySelectorAll('[data-track-dot]')];
    const isCompact = board.classList.contains('is-compact');

    phaseEls.forEach((el, index) => {
      const isProgressActive = progressIndex >= 0 && index <= progressIndex;
      const isProgressCurrent = index === progressIndex;

      el.classList.toggle('is-active', isProgressActive);
      el.classList.toggle('is-current', isProgressCurrent);

      if (!isCompact) {
        el.classList.remove('is-selected');
      }

      bottomPhases[index]?.classList.toggle('is-active', isProgressActive);
      dots[index]?.classList.toggle('is-active', isProgressActive);
      dots[index]?.classList.toggle('is-current', isProgressCurrent);
    });

    if (isCompact) {
      this.#applySelectedVisuals(board, this.#getSelectedPhaseIndex(board));
      this.#layoutTrackCompact(board, progressIndex);
    } else {
      this.#layoutTrackDesktop(board, progressIndex, now, phases);
    }
  }

  /**
   * @param {HTMLElement} board
   * @param {number} progressIndex
   * @param {number} now
   * @param {{ start: number, end: number }[]} phases
   */
  #layoutTrackDesktop(board, progressIndex, now, phases) {

    const phaseEls = [...board.querySelectorAll('[data-phase-start]')];
    const dots = [...board.querySelectorAll('[data-track-dot]')];
    const track = board.querySelector('.bfcm-event-timeline__track');
    if (!track) return;

    const trackRect = track.getBoundingClientRect();
    const trackWidth = trackRect.width;
    if (trackWidth <= 0) return;

    const anchorOffsets = phaseEls.map((el) => {
      const title = el.querySelector('.bfcm-event-timeline__phase-title') || el;
      const titleRect = title.getBoundingClientRect();
      return Math.max(0, titleRect.left - trackRect.left);
    });

    dots.forEach((dot, index) => {
      dot.style.position = 'absolute';
      dot.style.left = `${anchorOffsets[index] ?? 0}px`;
    });

    let fillPx = 0;

    if (progressIndex >= 0) {
      const count = phaseEls.length;
      const isPastCampaign = progressIndex === count - 1 && now > phases[count - 1].end;

      if (isPastCampaign) {
        fillPx = trackWidth;
      } else if (progressIndex < count - 1) {
        fillPx = anchorOffsets[progressIndex + 1];
      } else {
        fillPx = trackWidth;
      }

      board.classList.toggle('is-progress-gradual', !isPastCampaign && progressIndex < count - 1);
    } else {
      board.classList.remove('is-progress-gradual');
    }

    board.style.setProperty('--bfcm-timeline-progress', `${fillPx}px`);
  }

  /**
   * Equal-thirds dot layout for compact MO/TA (Figma Timeline / compact mobile).
   * @param {HTMLElement} board
   * @param {number} progressIndex
   */
  #layoutTrackCompact(board, progressIndex) {
    const track = board.querySelector('.bfcm-event-timeline__track');
    const dots = [...board.querySelectorAll('[data-track-dot]')];
    if (!track || dots.length === 0) return;

    const trackWidth = track.getBoundingClientRect().width;
    if (trackWidth <= 0) return;

    const count = dots.length;
    const compactDot = 12;
    const centers = dots.map((_, index) => {
      const fraction = (index + 0.5) / count;
      return fraction * trackWidth - compactDot / 2;
    });

    dots.forEach((dot) => {
      dot.style.removeProperty('left');
      dot.style.position = 'relative';
    });

    let fillPx = 0;

    if (progressIndex >= 0) {
      const safeIndex = Math.min(Math.max(progressIndex, 0), count - 1);
      fillPx = centers[safeIndex] + compactDot / 2;
      if (safeIndex === count - 1) {
        fillPx = trackWidth;
      }
      board.classList.toggle('is-progress-gradual', safeIndex < count - 1);
    } else {
      board.classList.remove('is-progress-gradual');
    }

    board.style.setProperty('--bfcm-timeline-progress', `${fillPx}px`);
  }
}

/**
 * @param {string} value - YYYY-MM-DD
 * @param {boolean} endOfDay
 * @returns {number}
 */
function parseDateBoundary(value, endOfDay) {
  if (!value) return Number.NaN;
  const trimmed = value.trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!match) return Number.NaN;
  const year = Number(match[1]);
  const month = Number(match[2]) - 1;
  const day = Number(match[3]);
  if (endOfDay) {
    return new Date(year, month, day, 23, 59, 59, 999).getTime();
  }
  return new Date(year, month, day, 0, 0, 0, 0).getTime();
}

if (!customElements.get('bfcm-event-timeline')) {
  customElements.define('bfcm-event-timeline', BfcmEventTimeline);
}
